import dayjs from 'dayjs';
import { store } from 'store';

import { tponeUrl } from '~constants/tpone';

import { catchAllEndDate, dateDisplayFormat, deriveDuration, formatDateString, formatTimeString } from '~libs/dayjs';
import { validatePackageServiceTypeCompatibility } from '~appointments/lib/validate';

type Derive = (values: any) => any;
type ReadOnly = (values: any) => string | string[];

export const deriveClientAddress: Derive = ({ client: { address, suburb } = { address: '', suburb: '' } }) =>
  address !== '' ? `${address}, ${suburb}` : '';

export const deriveClientDescription: Derive = ({ client_id }) => {
  const state = store.getState();
  const { clients } = state.main;
  const client = clients.rows.find(({ id }) => parseInt(client_id) === id);
  return client?.description;
};

export const deriveClientLink: Derive = ({ client_id }) => {
  const REDIRECT_URL = tponeUrl;
  return `${REDIRECT_URL}/client-details.asp?eid=${client_id}`;
};

export const deriveClientPackageName: Derive = ({ client_id }) => {
  const state = store.getState();
  const { clients } = state.main;
  const client = clients.rows.find(({ id }) => parseInt(client_id) === id);
  return client?.default_package?.name ?? 'No default package';
};

export const deriveServiceTypeDescription: Derive = ({ service_type_id, package_fixed, package_fixed_id }) => {
  const isValid = validatePackageServiceTypeCompatibility({ service_type_id, package_fixed, package_fixed_id });

  return isValid ? '' : 'Package and service type are mismatched';
};

export const deriveAccountsDefinition: Derive = ({ accounts_definitions, client_id, service_type_id }) => ({
  ...accounts_definitions,
  client_id,
  service_type_id,
});

export const deriveAccountSettings: Derive = ({
  chargeable_travel,
  chargeable_travel_type,
  break_duration,
  leave_entry_id,
  exclude_from_payroll,
}) => ({
  chargeable_travel,
  chargeable_travel_type,
  break_duration,
  leave_entry_id,
  exclude_from_payroll,
});

export const deriveActualTimes: Derive = ({ start_time_actual, end_time_actual }) => ({
  start_time_actual,
  end_time_actual,
});

export const deriveChargeableCancellation: Derive = ({
  chargeable_cancellation,
  chargeable_cancellation_details,
  duration,
}) => ({
  chargeable_cancellation,
  chargeable_cancellation_details,
  duration,
});

export const deriveDistanceTravelled: Derive = ({ distance_to_client, distance_with_client }) => ({
  distance_to_client,
  distance_with_client,
});

export const deriveTimeMarked: Derive = ({ start_time_marked, end_time_marked }) => ({
  start_time_marked: start_time_marked !== null ? formatTimeString(start_time_marked) : start_time_marked,
  end_time_marked: end_time_marked !== null ? formatTimeString(end_time_marked) : end_time_marked,
});

export const deriveServiceTypeSelectorParams: Derive = ({
  package_fixed_id = null,
  transport = false,
  case_management = false,
}) => {
  const params: any = {};
  if (transport) params.allow_transport = true;
  if (package_fixed_id) params.package_id = package_fixed_id;
  if (case_management) params.allow_case_management = true;
  return params;
};

export const deriveServiceTypeSupportCoordinationParams: Derive = () => {
  const params: any = {};
  const state = store.getState();
  const {
    login: {
      settings: { SYSTEM_SupportCoordinationServiceTypeID },
    },
  } = state.main;
  if (
    SYSTEM_SupportCoordinationServiceTypeID &&
    (Array.isArray(SYSTEM_SupportCoordinationServiceTypeID) ||
      Number.parseInt(SYSTEM_SupportCoordinationServiceTypeID as string))
  ) {
    params.id = SYSTEM_SupportCoordinationServiceTypeID;
  }

  return params;
};

export const derivePaymentAmount: (values: any) => any = (values) => {
  const { start_time, end_time, ends_next_day, service_type_id } = values;

  if (!start_time || !end_time || !service_type_id) {
    return;
  }

  const duration = deriveDuration({ start_time, end_time, ends_next_day });

  const state = store.getState();
  const { serviceTypes } = state.appointments;
  const serviceType = serviceTypes.rows.find(({ id }) => parseInt(service_type_id) === id);

  const { default_rate, billing } = serviceType ?? {};

  switch (billing) {
    case 'Hourly': {
      return default_rate ? (default_rate * duration) / 60 : 0;
    }
    default:
    case 'Fixed': {
      return default_rate ?? 0;
    }
  }
};

export const deriveReadOnlyDate: ReadOnly = ({ date }) => dayjs(date).format(dateDisplayFormat);

const placeholder = 'Not entered';

export const readOnlyStartEndTime: ReadOnly = ({ start_time, end_time }) =>
  `${formatTimeString(start_time)} - ${formatTimeString(end_time)}`;

export const readOnlyTimeMarked: ReadOnly = ({ start_time_marked, end_time_marked }) => [
  start_time_marked !== null ? formatTimeString(start_time_marked) : 'Start time not set',
  end_time_marked !== null ? formatTimeString(end_time_marked) : 'End time not set',
];

export const readOnlyTimeActual: ReadOnly = ({ start_time_actual, end_time_actual }) => [
  `Start: ${start_time_actual !== null ? formatTimeString(start_time_actual) : placeholder}`,
  `End: ${end_time_actual !== null ? formatTimeString(end_time_actual) : placeholder}`,
];

export const readOnlyDistanceTravelled: ReadOnly = ({ distance_to_client, distance_with_client }) => [
  `TO Client: ${distance_to_client !== null ? `${distance_to_client} kms` : placeholder}`,
  `WITH Client: ${distance_with_client !== null ? `${distance_with_client} kms` : placeholder}`,
];

export const readOnlyTimeTravelled: ReadOnly = ({ travel_1 }) => (!travel_1 ? placeholder : `${travel_1} mins`);

export const readOnlyChargeableCancellation: ReadOnly = ({ chargeable_cancellation_details }) => {
  const { client = null, client_unit = null, carer = null } = chargeable_cancellation_details ?? {};
  return [`Charge Client: ${client ?? '-'} ${client_unit ?? ''}`, `Pay Care Worker: ${carer ?? '-'} mins`];
};

export const readOnlyRepeatDates: ReadOnly = ({ repeat_start, repeat_end }) => [
  `Start: ${repeat_start !== null ? formatDateString(repeat_start) : '-'}`,
  `End: ${repeat_end !== null && dayjs(catchAllEndDate) > dayjs(repeat_end) ? formatDateString(repeat_end) : '-'}`,
];

export const readOnlyRepeatFrequency: ReadOnly = ({ repeat_frequency, skip_on_public_holidays }) => [
  `${repeat_frequency?.day}, ${repeat_frequency?.frequency}`,
  `Skip public holidays - ${skip_on_public_holidays ? 'Yes' : 'No'}`,
];
